import React, { useState, useCallback } from 'react'
import { InteractiveDialog, InstructionsPure, ButtonHLPure } from '../../pures'
import INEDetector from '../../features/ProofOfLife/INEDetector/INEDetector'
import { dataURLtoFile } from '../../../utils/image.util'
import './modal-camera-capture.css'

/**
 * @typedef {Object} ModalCameraCaptureProps
 * @property {function} t - Función de traducción i18n
 * @property {function} onCapture - Callback cuando se confirma la foto. Recibe (syntheticEvent, documentSelected, dataURL)
 * @property {function} onClose - Callback para cerrar/cancelar (volver al modal anterior)
 * @property {Object} documentSelected - Documento seleccionado actualmente
 */

/**
 * @function ModalCameraCapture
 * @description Modal para capturar foto de la INE con la cámara del dispositivo.
 * Contiene dos estados: 'camera' (cámara activa con detección automática o manual) y 'preview'
 * (vista previa de la imagen capturada con opciones de reintentar o confirmar).
 * Permite alternar entre captura automática y manual mediante un toggle.
 * @param {ModalCameraCaptureProps} props
 * @returns {JSX.Element}
 */
const ModalCameraCapture = ({ t, onCapture, onClose, documentSelected }) => {
  /** @type {['camera'|'preview', function]} Estado del flujo */
  const [phase, setPhase] = useState('camera')
  /** @type {[string|null, function]} Data URL de la imagen capturada */
  const [capturedImage, setCapturedImage] = useState(null)
  /** @type {[number, function]} Key para forzar re-mount del INEDetector */
  const [detectorKey, setDetectorKey] = useState(0)
  /** @type {['auto'|'manual', function]} Modo de captura seleccionado */
  const [captureMode, setCaptureMode] = useState('auto')

  /**
   * @function handleINEDetected
   * @description Callback del INEDetector cuando detecta y captura la INE
   * @param {{ image: string, timestamp: string, wasRotated: boolean }} result
   * @returns {void}
   */
  const handleINEDetected = useCallback((result) => {
    setCapturedImage(result.image)
    setPhase('preview')
  }, [])

  /**
   * @function handleRetry
   * @description Reinicia la cámara para volver a capturar
   * @returns {void}
   */
  const handleRetry = useCallback(() => {
    setCapturedImage(null)
    setPhase('camera')
    setDetectorKey((prev) => prev + 1)
  }, [])

  /**
   * @function handleConfirm
   * @description Confirma la foto capturada y la envía al flujo principal
   * @returns {void}
   */
  const handleConfirm = useCallback(() => {
    if (!capturedImage) return

    const file = dataURLtoFile(capturedImage, `INE_${Date.now()}.jpg`)
    const syntheticEvent = {
      target: {
        files: [file]
      }
    }
    onCapture(syntheticEvent, documentSelected, capturedImage)
  }, [capturedImage, documentSelected, onCapture])

  /**
   * @function toggleCaptureMode
   * @description Alterna entre modo de captura automática y manual
   * @returns {void}
   */
  const toggleCaptureMode = useCallback(() => {
    setCaptureMode((prev) => (prev === 'auto' ? 'manual' : 'auto'))
  }, [])

  return (
    <InteractiveDialog
      title={t('uploadIdentityDocument.modals.camera.title')}
      onClose={onClose}
      dialogSize="G"
      CustomButtons={
        <div className="modalCameraCapture-buttons">
          <ButtonHLPure
            nameBtn={t('button.cancel')}
            onClickBtn={onClose}
          />
          {phase === 'preview' && (
            <>
              <ButtonHLPure
                nameBtn={t('uploadIdentityDocument.modals.camera.retry')}
                onClickBtn={handleRetry}
              />
              <ButtonHLPure
                nameBtn={t('uploadIdentityDocument.modals.camera.confirm')}
                primary
                onClickBtn={handleConfirm}
              />
            </>
          )}
        </div>
      }
    >
      <div className="modalCameraCapture-container">
        <InstructionsPure
          instructions={[t('uploadIdentityDocument.modals.camera.instructions')]}
        />

        {phase === 'camera' && (
          <>
            <div className="modalCameraCapture-mode-toggle">
              <button
                type="button"
                className={`modalCameraCapture-mode-btn ${captureMode === 'auto' ? 'modalCameraCapture-mode-btn--active' : ''}`}
                onClick={captureMode !== 'auto' ? toggleCaptureMode : undefined}
              >
                {t('uploadIdentityDocument.modals.camera.captureAuto')}
              </button>
              <button
                type="button"
                className={`modalCameraCapture-mode-btn ${captureMode === 'manual' ? 'modalCameraCapture-mode-btn--active' : ''}`}
                onClick={captureMode !== 'manual' ? toggleCaptureMode : undefined}
              >
                {t('uploadIdentityDocument.modals.camera.captureManual')}
              </button>
            </div>

            <div className="modalCameraCapture-camera-wrapper">
              <INEDetector
                key={detectorKey}
                onINEDetected={handleINEDetected}
                onClose={onClose}
                t={t}
                embedded
                captureMode={captureMode}
              />
            </div>
          </>
        )}

        {phase === 'preview' && capturedImage && (
          <div className="modalCameraCapture-preview-wrapper">
            <div className="modalCameraCapture-preview-frame">
              <img
                src={capturedImage}
                alt="INE capturada"
                className="modalCameraCapture-preview-image"
              />
            </div>
          </div>
        )}
      </div>
    </InteractiveDialog>
  )
}

export { ModalCameraCapture as default }

